import React, { useCallback, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ChevronLeft, Lock, Plus, ShieldCheck, Trash2, Upload } from "lucide-react";
import SuccessToast from "../components/SuccessToast";
import VenuePolicyEditor from "./VenuePolicyEditor";
import AdminTopBar from "./AdminTopBar";
import SearchableSelect from "./SearchableSelect";
import { createAdminNotification } from "./notificationsService";
import {
  getDateInputMinYmd,
  toDateOnlyYmd,
} from "../lib/dateTime";
import {
  getDateError,
  getTicketNameError,
  getTicketPriceError,
  getTicketQuantityError,
  getTicketSectionError,
  getTitleError,
  getVenueError,
  validateEventCore,
  validateTicketTierBasics,
} from "../lib/eventFormValidation";
import {
  getTopLevelSections,
  buildPreviewPlanForParent,
  findSeatAreaOverlap,
  resolveSeatNumber,
} from "../lib/venueLayout";
import {
  buildMusicMuseumPreviewPlan,
  calculateTierSeatQuantity,
  findSnapshotSectionLayout,
  getTierSeatAreaOptions,
  parseLegacyTierSection,
  parseSeatLayoutSnapshot,
  tierUsesAutoQuantity,
} from "../lib/eventSeatLayout";
import {
  buildSnapshotForVenueId,
  fetchActiveVenues,
  fetchVenuePolicyHtml,
  fetchVenueWithLayout,
  isMissingPolicyColumn,
  isMissingVenueColumn,
} from "../lib/venuesApi";
import { normalizeVenuePolicyHtml, resolveEventPolicyHtml } from "../lib/venuePolicyHtml";
import {
  applySectionColorsToSnapshot,
  buildSectionColorOverrides,
  DEFAULT_SECTION_COLOR,
  getVenueSectionColor,
  isMissingColorColumn,
  normalizeHex,
} from "../lib/sectionColor";

const CreateEvent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");
  const requestCode = searchParams.get("requestCode");
  const isEditMode = Boolean(eventId);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(isEditMode);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const editSnapshotRef = useRef(null);
  const fieldsDisabled = isEditMode && isLocked;

  const VENUE_OPTIONS = [
    { value: "Music Museum", label: "Music Museum" },
    { value: "Teatrino", label: "Teatrino" },
    { value: "NewPort Performing Arts Theater", label: "NewPort Performing Arts Theater" },
  ];

  const VENUE_SECTION_OPTIONS = {
    "Music Museum": [
      { value: "Orchestra", label: "Orchestra" },
      { value: "VIP", label: "VIP" },
      { value: "Balcony", label: "Balcony" },
    ],
    Teatrino: [
      { value: "Stalls", label: "Stalls" },
      { value: "Mezzanine", label: "Mezzanine" },
      { value: "Balcony", label: "Balcony" },
    ],
    "NewPort Performing Arts Theater": [
      { value: "Orchestra", label: "Orchestra" },
      { value: "Mezzanine", label: "Mezzanine" },
      { value: "Balcony", label: "Balcony" },
    ],
  };

  const [showSeatPlanModal, setShowSeatPlanModal] = useState(false);
  const [seatPlanPreviewSection, setSeatPlanPreviewSection] = useState("");
  const [seatPlanPreviewAreas, setSeatPlanPreviewAreas] = useState([]);
  const [seatPlanPageIndex, setSeatPlanPageIndex] = useState(0);
  const [managedVenues, setManagedVenues] = useState([]);
  const [venueLayout, setVenueLayout] = useState(null);
  const [savedVenueId, setSavedVenueId] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const currentSeatPlan = (() => {
    const parentSection = seatPlanPreviewSection;
    const previewAreas = seatPlanPreviewAreas;
    const tierLike = { section: parentSection, seat_areas: previewAreas };

    const snapshotLayout = findSnapshotSectionLayout(savedSnapshot, tierLike);
    if (snapshotLayout?.pages?.length) {
      return {
        label: snapshotLayout.label,
        description: snapshotLayout.description || "Saved event seat layout",
        pages: snapshotLayout.pages,
        canonical: true,
      };
    }

    if (venueLayout && parentSection) {
      const venuePlan = buildPreviewPlanForParent({
        sections: venueLayout.sections || [],
        seats: venueLayout.seats || [],
        parentName: parentSection,
        seatAreas: previewAreas,
      });
      if (venuePlan) return venuePlan;
    }

    return buildMusicMuseumPreviewPlan(parentSection, previewAreas);
  })();
  const currentSeatPage = currentSeatPlan?.pages ? currentSeatPlan.pages[seatPlanPageIndex] : currentSeatPlan;
  const previewRowCount = currentSeatPage?.rows?.length || 0;
  const previewColCount = currentSeatPage?.columns || 0;
  const previewSeatLabel = (row, rowIndex, colIndex) => {
    const seatNumber = resolveSeatNumber(currentSeatPage, rowIndex, colIndex) ?? colIndex + 1;
    return `${row}${seatNumber}`;
  };

  const getPreviewSeatSize = () => {
    if (previewRowCount >= 15 || previewColCount >= 24) return 14;
    if (previewRowCount >= 12 || previewColCount >= 20) return 16;
    if (previewRowCount >= 10 || previewColCount >= 16) return 18;
    return 22;
  };

  const previewSeatSize = getPreviewSeatSize();
  const previewSeatGap = previewSeatSize <= 14 ? 4 : 6;
  const previewSeatFontSize = previewSeatSize <= 14 ? 7 : 8;
  const previewRowLabelWidth = previewSeatSize <= 14 ? 16 : 20;
  const previewRowLabelFontSize = previewSeatSize <= 14 ? 9 : 10;

  React.useEffect(() => {
    setSeatPlanPageIndex(0);
  }, [seatPlanPreviewSection, seatPlanPreviewAreas]);

  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [isProjectLinkLocked, setIsProjectLinkLocked] = useState(false);
  const [originalEventDate, setOriginalEventDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [seatPlanImageFile, setSeatPlanImageFile] = useState(null);
  const [seatPlanImagePreview, setSeatPlanImagePreview] = useState("");
  const [existingSeatPlanImageUrl, setExistingSeatPlanImageUrl] = useState("");
  const [seatPlanImageUrlInput, setSeatPlanImageUrlInput] = useState("");
  const [seatPlanImageOverridden, setSeatPlanImageOverridden] = useState(false);
  const [inheritedSeatPlanFromVenueId, setInheritedSeatPlanFromVenueId] = useState("");
  const [linkedRequestCode, setLinkedRequestCode] = useState(requestCode || "");

  // Project picker state (create mode only)
  const [acceptedProjects, setAcceptedProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [eventPolicyHtml, setEventPolicyHtml] = useState("");
  const [policyContentKey, setPolicyContentKey] = useState(isEditMode ? `event:${eventId}` : "none");

  const [eventData, setEventData] = useState({
    title: "",
    subtitle: "",
    description: "",
    date: "",
    time: "",
    venue: "",
    venueId: "",
    address: "",
    status: "draft"
  });

  const [ticketTiers, setTicketTiers] = useState([
    { name: "VIP", price: "", quantity: "", section: "", seatAreas: [], color: "" },
    { name: "Orchestra", price: "", quantity: "", section: "", seatAreas: [], color: "" }
  ]);

  const getAutoQuantityForTier = (tier) =>
    String(
      calculateTierSeatQuantity({
        tier,
        venueLayout,
        snapshot: savedSnapshot,
        venueName: eventData.venue,
      })
    );

  const getSeatAreasForTier = (tier) =>
    getTierSeatAreaOptions({
      tier,
      venueLayout,
      venueName: eventData.venue,
    });

  const tierHasAutoQuantity = (tier) =>
    tierUsesAutoQuantity({
      tier,
      venueLayout,
      venueName: eventData.venue,
    });

  const inferSectionValue = (tierRow = {}, venue = "") => {
    const directSection = String(tierRow.section || "").trim();
    if (directSection) return directSection;

    const tierName = String(tierRow.name || "").trim();
    if (!tierName) return "";

    const normalized = tierName.toLowerCase();

    // Backward compatibility for old rows where section was not saved.
    if (venue === "Music Museum") {
      if (normalized.includes("orchestra")) return "Orchestra";
      if (normalized.includes("vip")) return "VIP";
      if (normalized.includes("balcony")) return "Balcony";
    }

    if (venue === "Teatrino") {
      if (normalized.includes("stalls")) return "Stalls";
      if (normalized.includes("mezzanine")) return "Mezzanine";
      if (normalized.includes("balcony")) return "Balcony";
    }

    if (venue === "NewPort Performing Arts Theater") {
      if (normalized.includes("orchestra")) return "Orchestra";
      if (normalized.includes("mezzanine")) return "Mezzanine";
      if (normalized.includes("balcony")) return "Balcony";
    }

    return "";
  };

  const venueLocked = isEditMode && Boolean(savedVenueId || savedSnapshot);
  const venueSelectOptions = managedVenues.length
    ? managedVenues.map((venueRow) => ({ value: venueRow.id, label: venueRow.name }))
    : VENUE_OPTIONS;
  const managedVenueSectionOptions = venueLayout
    ? getTopLevelSections(venueLayout.sections || [])
    : [];
  const fallbackSectionOptions = eventData.venue
    ? VENUE_SECTION_OPTIONS[eventData.venue] || []
    : VENUE_SECTION_OPTIONS["Music Museum"];
  const sectionOptions = managedVenueSectionOptions.length
    ? managedVenueSectionOptions
    : fallbackSectionOptions;

  const canViewSeatPlan = (tier) => {
    const sectionValue = tier?.section;
    if (!sectionValue) return false;

    const previewAreas = tier.seatAreas?.length
      ? tier.seatAreas
      : getSeatAreasForTier(tier).map((option) => option.value);

    const tierLike = { section: sectionValue, seat_areas: previewAreas };

    if (findSnapshotSectionLayout(savedSnapshot, tierLike)?.pages?.length) return true;

    if (venueLayout) {
      const venuePlan = buildPreviewPlanForParent({
        sections: venueLayout.sections || [],
        seats: venueLayout.seats || [],
        parentName: sectionValue,
        seatAreas: previewAreas,
      });
      if (venuePlan?.pages?.length) return true;
    }

    return Boolean(buildMusicMuseumPreviewPlan(sectionValue, previewAreas));
  };

  const focusField = (field) => {
    if (!field) return;
    window.requestAnimationFrame(() => {
      const node = document.querySelector(`[data-field="${field}"]`);
      if (!node) return;
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof node.focus === "function") node.focus();
    });
  };

  const setInlineError = (field, message) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
    if (field === "date") setDateError(message || "");
  };

  const showFieldError = (field, message) => {
    setInlineError(field, message);
    showErrorToast(message);
    focusField(field);
  };

  const validateTicketTiers = () => {
    if (!ticketTiers.length) {
      return { field: "tickets", message: "Add at least one ticket tier." };
    }

    const basicsError = validateTicketTierBasics(ticketTiers, {
      hasAutoQuantity: tierHasAutoQuantity,
    });
    if (basicsError) return basicsError;

    for (let index = 0; index < ticketTiers.length; index += 1) {
      const tier = ticketTiers[index];
      const tierLabel = tier.name || tier.section || "ticket tier";
      if (!String(tier.section || "").trim()) {
        return { field: `ticket-section-${index}`, message: `Select a section for ${tierLabel}.`, index };
      }

      const areaOptions = getSeatAreasForTier(tier);
      if (areaOptions.length) {
        if (!tier.seatAreas?.length) {
          return { field: `ticket-section-${index}`, message: `Select at least one seat area for ${tierLabel}.`, index };
        }

        const qty = calculateTierSeatQuantity({
          tier,
          venueLayout,
          snapshot: savedSnapshot,
          venueName: eventData.venue,
        });
        if (qty <= 0) {
          return { field: `ticket-qty-${index}`, message: `Selected seat areas for ${tierLabel} have no physical seats.`, index };
        }
      }
    }

    const overlap = findSeatAreaOverlap(ticketTiers);
    if (overlap) {
      return { field: "tickets", message: `${overlap.areaLabel} is already assigned to another ticket tier.` };
    }

    return null;
  };

  const showErrorToast = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => {
      setErrorToastMessage("");
    }, 3000);
  };

  const loadEventForEdit = useCallback(
    async () => {
      if (!isEditMode) return;

      const [{ data: eventRow, error: eventError }, { data: tierRows, error: tierError }] = await Promise.all([
        supabase
          .from("events")
          .select("*")
          .eq("id", eventId)
          .single(),
        supabase
          .from("ticket_tiers")
          .select("*")
          .eq("event_id", eventId)
          .order("id", { ascending: true }),
      ]);

      if (eventError || tierError) {
        console.error("Failed to load event for editing:", eventError || tierError);
        showErrorToast("Failed to load event data for editing");
        setInitialLoading(false);
        return;
      }

      setEventData({
        title: eventRow?.title || "",
        subtitle: eventRow?.subtitle || "",
        description: eventRow?.description || "",
        date: eventRow?.date || "",
        time: eventRow?.time || "",
        venue: eventRow?.venue || "",
        venueId: eventRow?.venue_id || "",
        address: eventRow?.address || "",
        status: eventRow?.status === "upcoming" ? "published" : (eventRow?.status || "draft"),
      });
      setSavedVenueId(eventRow?.venue_id || "");
      setSavedSnapshot(parseSeatLayoutSnapshot(eventRow?.seat_layout_snapshot));
      setEventPolicyHtml(resolveEventPolicyHtml(eventRow));
      setPolicyContentKey(`event:${eventId}`);
      setOriginalEventDate(toDateOnlyYmd(eventRow?.date || ""));
      setDateError("");

      const savedStatus = String(eventRow?.status || "").toLowerCase();
      setIsProjectLinkLocked(savedStatus === "published" || savedStatus === "upcoming");

      setLinkedRequestCode(eventRow?.booking_request_code || requestCode || "");

      setExistingImageUrl(eventRow?.image_url || "");
      setImagePreview(eventRow?.image_url || "");
      setExistingSeatPlanImageUrl(eventRow?.seat_plan_image_url || "");
      setSeatPlanImagePreview(eventRow?.seat_plan_image_url || "");
      setSeatPlanImageUrlInput(eventRow?.seat_plan_image_url || "");

      if (tierRows?.length) {
        setTicketTiers(
          tierRows.map((tier) => {
            const parsed = parseLegacyTierSection(inferSectionValue(tier, eventRow?.venue || ""));
            const seatAreas = Array.isArray(tier.seat_areas)
              ? tier.seat_areas.map(String)
              : parsed.seatAreas;
            const section = tier.section
              ? String(tier.section).includes(" / ")
                ? parseLegacyTierSection(tier.section).section
                : tier.section
              : parsed.section;

            const nextTier = {
              id: tier.id,
              name: tier.name || "",
              price: String(tier.price || ""),
              quantity: String(tier.quantity_total || ""),
              section: section || "",
              seatAreas,
              color: normalizeHex(tier.color) || "",
            };

            return nextTier;
          })
        );
      }

      setInitialLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventId, isEditMode, requestCode]
  );

  React.useEffect(() => {
    loadEventForEdit();
  }, [loadEventForEdit]);

  React.useEffect(() => {
    let isMounted = true;
    fetchActiveVenues(supabase)
      .then((rows) => {
        if (isMounted) setManagedVenues(rows);
      })
      .catch((error) => {
        console.warn("Reusable venues are unavailable; using built-in venue names.", error);
        if (isMounted) setManagedVenues([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const venueId = eventData.venueId;
    if (!venueId) {
      setVenueLayout(null);
      return undefined;
    }
    let isMounted = true;
    fetchVenueWithLayout(supabase, venueId)
      .then((layout) => {
        if (isMounted) setVenueLayout(layout);
      })
      .catch((error) => {
        console.warn("Failed to load venue layout:", error);
        if (isMounted) setVenueLayout(null);
      });
    return () => {
      isMounted = false;
    };
  }, [eventData.venueId]);

  // Create mode: copy the selected venue's seat plan image as a one-time event default.
  React.useEffect(() => {
    if (isEditMode) return;

    const venueId = String(eventData.venueId || "").trim();
    if (!venueId) {
      if (!inheritedSeatPlanFromVenueId) return;
      setInheritedSeatPlanFromVenueId("");
      setSeatPlanImageOverridden(false);
      setSeatPlanImageFile(null);
      setSeatPlanImageUrlInput("");
      setExistingSeatPlanImageUrl("");
      setSeatPlanImagePreview("");
      return;
    }

    if (!managedVenues.length) return;
    if (venueId === inheritedSeatPlanFromVenueId) return;

    const selected = managedVenues.find((venueRow) => venueRow.id === venueId);
    const url = String(selected?.seat_plan_image_url || "").trim();
    setSeatPlanImageFile(null);
    setSeatPlanImageUrlInput("");
    setSeatPlanImageOverridden(false);
    setExistingSeatPlanImageUrl(url);
    setSeatPlanImagePreview(url);
    setInheritedSeatPlanFromVenueId(venueId);
  }, [isEditMode, eventData.venueId, managedVenues, inheritedSeatPlanFromVenueId]);

  React.useEffect(() => {
    setTicketTiers((prev) =>
      prev.map((tier) => {
        if (!tierUsesAutoQuantity({ tier, venueLayout, venueName: eventData.venue })) {
          return tier;
        }

        return {
          ...tier,
          quantity: String(
            calculateTierSeatQuantity({
              tier,
              venueLayout,
              snapshot: savedSnapshot,
              venueName: eventData.venue,
            })
          ),
        };
      })
    );
  }, [venueLayout, savedSnapshot, eventData.venue]);

  React.useEffect(() => {
    if (!venueLayout?.sections?.length) return undefined;
    setTicketTiers((prev) =>
      prev.map((tier) => {
        if (!tier.section || normalizeHex(tier.color)) return tier;
        const venueColor = getVenueSectionColor(venueLayout.sections, tier.section);
        return venueColor ? { ...tier, color: venueColor } : tier;
      })
    );
  }, [venueLayout]);

  // Fetch projects for the picker in both create and edit mode.
  React.useEffect(() => {
    setProjectsLoading(true);
    supabase
      .from("bookings")
      .select("id, request_code, event_name, event_date, venue")
      .in("status", ["accepted", "approved", "event_preparation", "event_live"])
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setAcceptedProjects(data);
        setProjectsLoading(false);
      });
  }, []);

  // In edit mode, map saved booking_request_code back to a booking option.
  React.useEffect(() => {
    if (!linkedRequestCode || selectedProjectId || !acceptedProjects.length) return;
    const matched = acceptedProjects.find(
      (booking) => String(booking.request_code || "").trim() === String(linkedRequestCode || "").trim()
    );
    if (matched?.id) {
      setSelectedProjectId(String(matched.id));
    }
  }, [acceptedProjects, linkedRequestCode, selectedProjectId]);

  // Pre-fill form when navigating from ProjectDetails via router state
  React.useEffect(() => {
    if (isEditMode) return;
    const fromBooking = location.state?.fromBooking;
    if (!fromBooking) return;
    const rawDate = String(fromBooking.event_date || "");
    const date = rawDate.length >= 10 ? rawDate.slice(0, 10) : rawDate;
    const knownVenues = managedVenues.length
      ? managedVenues.map((venueRow) => venueRow.name)
      : VENUE_OPTIONS.map((o) => o.value);
    const matchedVenue = managedVenues.find((venueRow) => venueRow.name === fromBooking.venue);
    const venueMatch = matchedVenue?.name || (knownVenues.includes(fromBooking.venue || "") ? fromBooking.venue : "");
    setEventData((prev) => ({
      ...prev,
      title: fromBooking.event_name || prev.title,
      date: date || prev.date,
      venue: venueMatch || prev.venue,
      venueId: matchedVenue?.id || prev.venueId,
      address: matchedVenue?.address || prev.address,
    }));
    setLinkedRequestCode(fromBooking.request_code || "");
    setSelectedProjectId(String(fromBooking.id || ""));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, managedVenues]);

  React.useEffect(() => {
    if (isEditMode) return;
    const venueId = eventData.venueId;
    if (!venueId) {
      setEventPolicyHtml("");
      setPolicyContentKey("none");
      return;
    }

    let cancelled = false;
    const loadVenuePolicy = async () => {
      try {
        const html = normalizeVenuePolicyHtml(await fetchVenuePolicyHtml(supabase, venueId));
        if (cancelled) return;
        setEventPolicyHtml(html);
        setPolicyContentKey(`venue:${venueId}`);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load venue policy:", error);
        setEventPolicyHtml("");
        setPolicyContentKey(`venue:${venueId}`);
      }
    };

    loadVenuePolicy();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, eventData.venueId]);

  const handleVenueSelect = (value) => {
    if (venueLocked) return;
    let nextVenue = value;
    if (managedVenues.length) {
      const selected = managedVenues.find((venueRow) => venueRow.id === value);
      nextVenue = selected?.name || "";
      setEventData((prev) => ({
        ...prev,
        venueId: selected?.id || "",
        venue: nextVenue,
        address: selected?.address || prev.address,
      }));
    } else {
      setEventData((prev) => ({
        ...prev,
        venueId: "",
        venue: value,
      }));
    }
    setInlineError("venue", getVenueError(nextVenue, { allowEmpty: !nextVenue }));
  };

  const handleVenueBlur = () => {
    setInlineError("venue", getVenueError(eventData.venue));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value
    });

    if (name === "title") {
      setInlineError("title", getTitleError(value, { allowEmpty: !String(value || "").trim() }));
    }
    if (name === "date") {
      setInlineError("date", getDateError(value, {
        allowEmpty: !toDateOnlyYmd(value),
        isEditMode,
        originalEventDate,
      }));
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    if (name === "title") setInlineError("title", getTitleError(value));
    if (name === "date") {
      setInlineError("date", getDateError(value, { isEditMode, originalEventDate }));
    }
  };

  const projectOptions = useMemo(() => {
    const items = acceptedProjects.map((booking) => ({
      value: String(booking.id),
      label: `${booking.event_name || "Untitled Event"} — ${booking.request_code || `#${booking.id}`}`,
    }));
    return selectedProjectId ? [{ value: "", label: "No project linked" }, ...items] : items;
  }, [acceptedProjects, selectedProjectId]);

  const handleProjectSelect = (bookingId) => {
    if (isEditMode && isProjectLinkLocked) return;
    setSelectedProjectId(bookingId);
    if (!bookingId) return;
    const booking = acceptedProjects.find((b) => String(b.id) === String(bookingId));
    if (!booking) return;
    const rawDate = String(booking.event_date || "");
    const date = rawDate.length >= 10 ? rawDate.slice(0, 10) : rawDate;
    const knownVenues = managedVenues.length
      ? managedVenues.map((venueRow) => venueRow.name)
      : VENUE_OPTIONS.map((o) => o.value);
    const matchedVenue = managedVenues.find((venueRow) => venueRow.name === booking.venue);
    const venueMatch = matchedVenue?.name || (knownVenues.includes(booking.venue || "") ? booking.venue : "");
    setEventData((prev) => ({
      ...prev,
      title: booking.event_name || prev.title,
      date: date || prev.date,
      venue: venueMatch || prev.venue,
      venueId: matchedVenue?.id || prev.venueId,
      address: matchedVenue?.address || prev.address,
    }));
    setLinkedRequestCode(booking.request_code || "");
  };

  const handleTicketChange = (index, field, value) => {
    setTicketTiers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === "section") {
        updated[index].seatAreas = [];
        updated[index].color = getVenueSectionColor(venueLayout?.sections || [], value);
        if (tierUsesAutoQuantity({ tier: updated[index], venueLayout, venueName: eventData.venue })) {
          updated[index].quantity = "0";
        }
      }

      return updated;
    });

    const label = value || ticketTiers[index]?.name || ticketTiers[index]?.section || "ticket tier";
    if (field === "name") {
      setInlineError(`ticket-name-${index}`, getTicketNameError(value, { allowEmpty: !String(value || "").trim() }));
    }
    if (field === "price") {
      setInlineError(`ticket-price-${index}`, getTicketPriceError(value, { allowEmpty: !String(value || "").trim() }));
    }
    if (field === "quantity") {
      setInlineError(`ticket-qty-${index}`, getTicketQuantityError(value, { allowEmpty: !String(value || "").trim() }));
    }
    if (field === "section") {
      setInlineError(`ticket-section-${index}`, getTicketSectionError(value, {
        allowEmpty: !String(value || "").trim(),
        label,
      }));
    }
  };

  const handleTicketBlur = (index, field, value) => {
    const label = ticketTiers[index]?.name || ticketTiers[index]?.section || "ticket tier";
    if (field === "name") setInlineError(`ticket-name-${index}`, getTicketNameError(value));
    if (field === "price") setInlineError(`ticket-price-${index}`, getTicketPriceError(value));
    if (field === "quantity" && !tierHasAutoQuantity(ticketTiers[index])) {
      setInlineError(`ticket-qty-${index}`, getTicketQuantityError(value));
    }
    if (field === "section") setInlineError(`ticket-section-${index}`, getTicketSectionError(value, { label }));
  };

  const handleSeatAreaToggle = (index, areaLabel, checked) => {
    const current = ticketTiers[index] || {};
    const seatAreas = checked
      ? [...(current.seatAreas || []), areaLabel]
      : (current.seatAreas || []).filter((area) => area !== areaLabel);
    const nextTier = { ...current, seatAreas };
    nextTier.quantity = getAutoQuantityForTier(nextTier);

    setTicketTiers((prev) => {
      const updated = [...prev];
      updated[index] = nextTier;
      return updated;
    });

    const qty = Number(nextTier.quantity || 0);
    if (seatAreas.length && qty <= 0) {
      setInlineError(
        `ticket-qty-${index}`,
        `Selected seat areas for ${nextTier.name || "ticket tier"} have no physical seats.`
      );
    } else if (qty >= 1) {
      setInlineError(`ticket-qty-${index}`, "");
    }
  };

  const addTicketTier = () => {
    setTicketTiers([...ticketTiers, { name: "", price: "", quantity: "", section: "", seatAreas: [], color: "" }]);
  };

  const removeTicketTier = (index) => {
    setTicketTiers(ticketTiers.filter((_, i) => i !== index));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSeatPlanImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSeatPlanImageOverridden(true);
    setSeatPlanImageFile(file);
    setSeatPlanImagePreview(URL.createObjectURL(file));
    setSeatPlanImageUrlInput("");
  };

  const uploadImageToSupabase = async (file) => {
    const fileName = `${Date.now()}-${file.name}`;

    const { error } = await supabase.storage
      .from("event-images")
      .upload(fileName, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from("event-images")
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const buildEditSnapshot = () => ({
    eventData,
    ticketTiers,
    selectedProjectId,
    linkedRequestCode,
    existingImageUrl,
    imagePreview,
    existingSeatPlanImageUrl,
    seatPlanImagePreview,
    seatPlanImageUrlInput,
    seatPlanImageOverridden,
    eventPolicyHtml,
    policyContentKey,
  });

  const startEditing = () => {
    editSnapshotRef.current = buildEditSnapshot();
    setIsLocked(false);
  };

  const restoreSnapshot = () => {
    const snapshot = editSnapshotRef.current;
    if (!snapshot) return;

    setEventData(snapshot.eventData);
    setTicketTiers(snapshot.ticketTiers);
    setSelectedProjectId(snapshot.selectedProjectId);
    setLinkedRequestCode(snapshot.linkedRequestCode);
    setImageFile(null);
    setExistingImageUrl(snapshot.existingImageUrl);
    setImagePreview(snapshot.imagePreview);
    setSeatPlanImageFile(null);
    setExistingSeatPlanImageUrl(snapshot.existingSeatPlanImageUrl);
    setSeatPlanImagePreview(snapshot.seatPlanImagePreview);
    setSeatPlanImageUrlInput(snapshot.seatPlanImageUrlInput);
    setSeatPlanImageOverridden(snapshot.seatPlanImageOverridden);
    setEventPolicyHtml(snapshot.eventPolicyHtml || "");
    setPolicyContentKey(`${snapshot.policyContentKey || `event:${eventId}`}:restore:${Date.now()}`);
    setDateError("");
  };

  const cancelEditing = () => {
    restoreSnapshot();
    setIsLocked(true);
  };

  const hasUnsavedChanges = () => {
    const snapshot = editSnapshotRef.current;
    if (!snapshot) return false;

    if (imageFile || seatPlanImageFile) return true;
    if (seatPlanImageUrlInput !== snapshot.seatPlanImageUrlInput) return true;
    if (selectedProjectId !== snapshot.selectedProjectId) return true;
    if (
      normalizeVenuePolicyHtml(snapshot.eventPolicyHtml) !==
      normalizeVenuePolicyHtml(eventPolicyHtml)
    ) {
      return true;
    }
    if (JSON.stringify(eventData) !== JSON.stringify(snapshot.eventData)) return true;
    return JSON.stringify(ticketTiers) !== JSON.stringify(snapshot.ticketTiers);
  };

  const handleBack = () => {
    if (isEditMode && !isLocked && hasUnsavedChanges()) {
      setShowDiscardConfirm(true);
      return;
    }
    navigate(-1);
  };

  const validateBeforeSave = () => {
    setFieldErrors({});
    setDateError("");

    const coreError = validateEventCore({
      title: eventData.title,
      date: eventData.date,
      venue: eventData.venue,
      isEditMode,
      originalEventDate,
    });
    if (coreError) {
      showFieldError(coreError.field, coreError.message);
      return false;
    }

    const tierValidationError = validateTicketTiers();
    if (tierValidationError) {
      showFieldError(tierValidationError.field, tierValidationError.message);
      return false;
    }

    return true;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    if (!validateBeforeSave()) return;

    if (isEditMode) {
      setShowSaveConfirm(true);
      return;
    }

    handleSubmit();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateBeforeSave()) return;

    setLoading(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      console.log("AUTH USER:", userData.user);

      let imageUrl = "";
      let seatPlanImageUrl = seatPlanImageUrlInput.trim() || existingSeatPlanImageUrl;

      if (imageFile) {
        imageUrl = await uploadImageToSupabase(imageFile);
      }

      if (seatPlanImageFile) {
        seatPlanImageUrl = await uploadImageToSupabase(seatPlanImageFile);
      }

      const policySnapshot = normalizeVenuePolicyHtml(eventPolicyHtml) || null;
      const payload = {
        title: eventData.title,
        subtitle: eventData.subtitle,
        date: eventData.date,
        time: eventData.time,
        venue: eventData.venue,
        address: eventData.address,
        image_url: imageUrl || existingImageUrl,
        seat_plan_image_url: seatPlanImageUrl || null,
        booking_request_code: linkedRequestCode || null,
        status: eventData.status,
        venue_policy_snapshot: policySnapshot,
      };

      const colorOverrides = buildSectionColorOverrides(ticketTiers);
      if (isEditMode) {
        if (savedVenueId) payload.venue_id = savedVenueId;
        if (savedSnapshot) {
          payload.seat_layout_snapshot = applySectionColorsToSnapshot(savedSnapshot, colorOverrides);
        }
      } else if (eventData.venueId) {
        payload.venue_id = eventData.venueId;
        const snapshot = await buildSnapshotForVenueId(supabase, eventData.venueId, colorOverrides);
        if (snapshot) payload.seat_layout_snapshot = snapshot;
      }

      const payloadWithoutSeatPlanImage = { ...payload };
      delete payloadWithoutSeatPlanImage.seat_plan_image_url;
      const payloadWithoutVenueLayout = { ...payload };
      delete payloadWithoutVenueLayout.venue_id;
      delete payloadWithoutVenueLayout.seat_layout_snapshot;
      delete payloadWithoutVenueLayout.venue_policy_snapshot;
      const payloadWithoutPolicySnapshot = { ...payload };
      delete payloadWithoutPolicySnapshot.venue_policy_snapshot;

      let activeEventId = eventId;

      if (isEditMode) {
        let { error: updateError } = await supabase
          .from("events")
          .update(payload)
          .eq("id", eventId);

        const isSeatPlanColumnMissing =
          updateError &&
          (String(updateError.code || "") === "42703" ||
            String(updateError.message || "").toLowerCase().includes("seat_plan_image_url"));
        const isVenueLayoutColumnMissing = isMissingVenueColumn(updateError);
        const isPolicyColumnMissing = isMissingPolicyColumn(updateError);

        if (isSeatPlanColumnMissing) {
          const fallbackUpdate = await supabase
            .from("events")
            .update(payloadWithoutSeatPlanImage)
            .eq("id", eventId);
          updateError = fallbackUpdate.error;
        } else if (isPolicyColumnMissing) {
          const fallbackUpdate = await supabase
            .from("events")
            .update(payloadWithoutPolicySnapshot)
            .eq("id", eventId);
          updateError = fallbackUpdate.error;
        } else if (isVenueLayoutColumnMissing) {
          const fallbackUpdate = await supabase
            .from("events")
            .update(payloadWithoutVenueLayout)
            .eq("id", eventId);
          updateError = fallbackUpdate.error;
        }

        if (updateError) throw updateError;

        await createAdminNotification({
          title: "Event updated",
          message: `${eventData.title || "An event"} details were updated successfully.`,
          meta: "Events",
        });
      } else {
        let { data: insertedEvent, error } = await supabase
          .from("events")
          .insert({
            ...payload,
            status: eventData.status || "draft",
          })
          .select();

        const isSeatPlanColumnMissing =
          error &&
          (String(error.code || "") === "42703" ||
            String(error.message || "").toLowerCase().includes("seat_plan_image_url"));
        const isVenueLayoutColumnMissing = isMissingVenueColumn(error);
        const isPolicyColumnMissing = isMissingPolicyColumn(error);

        if (isSeatPlanColumnMissing) {
          const fallbackInsert = await supabase
            .from("events")
            .insert({
              ...payloadWithoutSeatPlanImage,
              status: eventData.status || "draft",
            })
            .select();

          insertedEvent = fallbackInsert.data;
          error = fallbackInsert.error;
        } else if (isPolicyColumnMissing) {
          const fallbackInsert = await supabase
            .from("events")
            .insert({
              ...payloadWithoutPolicySnapshot,
              status: eventData.status || "draft",
            })
            .select();

          insertedEvent = fallbackInsert.data;
          error = fallbackInsert.error;
        } else if (isVenueLayoutColumnMissing) {
          const fallbackInsert = await supabase
            .from("events")
            .insert({
              ...payloadWithoutVenueLayout,
              status: eventData.status || "draft",
            })
            .select();

          insertedEvent = fallbackInsert.data;
          error = fallbackInsert.error;
        }

        if (error) throw error;
        activeEventId = insertedEvent[0].id;

        const isPublished = String(eventData.status || "").toLowerCase() === "published";
        await createAdminNotification({
          title: isPublished ? "New event published" : "New event created",
          message: `${eventData.title || "An event"} was ${isPublished ? "published" : "saved as draft"}.`,
          meta: "Events",
        });
      }

      // Separate new tiers (no id) from existing tiers (has id)
      const mapTierForSave = (tier) => ({
        name: tier.name,
        price: parseFloat(tier.price || 0),
        quantity_total: tierHasAutoQuantity(tier)
          ? calculateTierSeatQuantity({
              tier,
              venueLayout,
              snapshot: savedSnapshot,
              venueName: eventData.venue,
            })
          : parseInt(tier.quantity || 0, 10),
        section: tier.section || null,
        seat_areas: tier.seatAreas?.length ? tier.seatAreas : null,
        color: normalizeHex(tier.color) || null,
      });

      const newTiers = ticketTiers.filter((tier) => !tier.id).map((tier) => ({
        event_id: activeEventId,
        ...mapTierForSave(tier),
      }));

      const existingTiers = ticketTiers.filter((tier) => tier.id).map((tier) => ({
        id: tier.id,
        event_id: activeEventId,
        ...mapTierForSave(tier),
      }));

      // Insert new tiers first
      if (newTiers.length > 0) {
        let { error: insertNewError } = await supabase.from("ticket_tiers").insert(newTiers);

        const isSeatAreasColumnMissing =
          insertNewError &&
          (String(insertNewError.code || "") === "42703" ||
            String(insertNewError.message || "").toLowerCase().includes("seat_areas"));

        if (isSeatAreasColumnMissing) {
          const fallbackTiers = newTiers.map(({ seat_areas, ...rest }) => rest);
          const fallbackResult = await supabase.from("ticket_tiers").insert(fallbackTiers);
          insertNewError = fallbackResult.error;
        }

        if (isMissingColorColumn(insertNewError)) {
          const fallbackTiers = newTiers.map(({ color, seat_areas, ...rest }) => rest);
          const fallbackResult = await supabase.from("ticket_tiers").insert(fallbackTiers);
          insertNewError = fallbackResult.error;
        }

        if (insertNewError) {
          console.error("Error inserting new tiers:", insertNewError);
          throw insertNewError;
        }
      }

      // Update existing tiers only if in edit mode AND tier data has changed
      if (isEditMode && existingTiers.length > 0) {
        // Fetch current tier data to check if anything actually changed
        const { data: currentTiers, error: fetchError } = await supabase
          .from("ticket_tiers")
          .select("*")
          .in("id", existingTiers.map(t => t.id));

        if (fetchError) {
          console.error("Error fetching current tiers:", fetchError);
          throw fetchError;
        }

        // Only update tiers where data actually changed
        const tiersToUpdate = existingTiers.filter(newTier => {
          const currentTier = currentTiers.find(t => t.id === newTier.id);
          const currentAreas = JSON.stringify(currentTier?.seat_areas || []);
          const nextAreas = JSON.stringify(newTier.seat_areas || []);
          return !currentTier ||
            currentTier.name !== newTier.name ||
            currentTier.price !== newTier.price ||
            currentTier.quantity_total !== newTier.quantity_total ||
            currentTier.section !== newTier.section ||
            currentAreas !== nextAreas ||
            normalizeHex(currentTier.color) !== normalizeHex(newTier.color);
        });

        if (tiersToUpdate.length > 0) {
          let { error: updateError } = await supabase
            .from("ticket_tiers")
            .upsert(tiersToUpdate, { onConflict: "id" });

          const isSeatAreasColumnMissing =
            updateError &&
            (String(updateError.code || "") === "42703" ||
              String(updateError.message || "").toLowerCase().includes("seat_areas"));

          if (isSeatAreasColumnMissing) {
            const fallbackTiers = tiersToUpdate.map(({ seat_areas, ...rest }) => rest);
            const fallbackResult = await supabase
              .from("ticket_tiers")
              .upsert(fallbackTiers, { onConflict: "id" });
            updateError = fallbackResult.error;
          }

          if (isMissingColorColumn(updateError)) {
            const fallbackTiers = tiersToUpdate.map(({ color, seat_areas, ...rest }) => rest);
            const fallbackResult = await supabase
              .from("ticket_tiers")
              .upsert(fallbackTiers, { onConflict: "id" });
            updateError = fallbackResult.error;
          }

          if (updateError) {
            console.error("Error updating tiers:", updateError);
            throw updateError;
          }
        }
      }

      if (isEditMode) {
        const { data: existingTierRows, error: existingTierError } = await supabase
          .from("ticket_tiers")
          .select("id")
          .eq("event_id", activeEventId);

        if (existingTierError) throw existingTierError;

        // Combine new and existing tier IDs to keep
        const tierIdsToKeep = [
          ...newTiers.map(t => t.id), // new tiers (should be undefined, but include for safety)
          ...existingTiers.map(t => t.id)
        ].filter(Boolean);

        const removedIds = (existingTierRows || [])
          .map((row) => row.id)
          .filter((existingId) => !tierIdsToKeep.includes(existingId));

        if (removedIds.length) {
          const { error: deleteRemovedError } = await supabase
            .from("ticket_tiers")
            .delete()
            .in("id", removedIds);

          if (deleteRemovedError) {
            console.error("Some removed tiers could not be deleted:", deleteRemovedError);
          }
        }
      }

      if (isEditMode) {
        setToastMessage("Event updated successfully");
        window.setTimeout(() => {
          setToastMessage("");
        }, 2500);
        setImageFile(null);
        setSeatPlanImageFile(null);
        editSnapshotRef.current = null;
        setIsLocked(true);
        await loadEventForEdit();
      } else {
        setToastMessage("Event published successfully");
        window.setTimeout(() => {
          navigate("/admin/liveevents");
        }, 900);
      }

    } catch (error) {
      console.error(error);
      showErrorToast(`Failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#f3f2f1]">
        <AdminTopBar />
        <div className="p-4 sm:p-6 md:p-10 flex items-center justify-center">
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Loading Event...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f2f1]">
      <AdminTopBar />
      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-6xl mx-auto min-w-0">
        <SuccessToast message={toastMessage} />
        <SuccessToast message={errorToastMessage} variant="error" />

        {/* HEADER */}
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-black font-bold text-sm uppercase self-start"
          >
            <ChevronLeft size={20} /> {isEditMode ? "Edit Event Posting" : "Create New Event Posting"}
          </button>

          {isEditMode && (
            isLocked ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <p className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                  <Lock size={14} /> Click Edit Event to make changes.
                </p>
                <button
                  type="button"
                  onClick={startEditing}
                  className="bg-brand-maroon text-white min-h-11 px-6 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-red-700"
                >
                  Edit Event
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={loading}
                  className="min-h-11 px-6 rounded-xl border border-gray-300 bg-white text-gray-700 font-black uppercase text-xs tracking-widest hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setShowSaveConfirm(true)}
                  disabled={loading}
                  className="bg-brand-maroon text-white min-h-11 px-6 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-red-700 disabled:opacity-60"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )
          )}
        </div>

        <form onSubmit={handleFormSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* PROJECT PICKER */}
          <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">
              Link to a Project
            </p>
            <SearchableSelect
              value={selectedProjectId}
              onChange={handleProjectSelect}
              options={projectOptions}
              pinnedOption=""
              placeholder={
                projectsLoading
                  ? "Loading accepted projects…"
                  : acceptedProjects.length
                  ? "Select an accepted project to auto-fill the form…"
                  : "No accepted projects found"
              }
              searchPlaceholder="Search by event name or request code..."
              emptyLabel="No matching projects"
              disabled={fieldsDisabled || projectsLoading || (isEditMode && isProjectLinkLocked)}
              size="md"
              className="w-full"
            />
            {selectedProjectId ? (
              <p className="mt-2 text-xs font-medium text-green-700">
                ✓ Project linked. You can still edit any field below.
              </p>
            ) : linkedRequestCode ? (
              <p className="mt-2 text-xs font-medium text-amber-700">
                Linked request code: {linkedRequestCode} (not found in current project list).
              </p>
            ) : null}
            {isEditMode && isProjectLinkLocked && (
              <p className="mt-2 text-xs font-medium text-gray-600">
                Project link is locked because this event is already published.
              </p>
            )}
          </div>

          {/* LEFT */}
          <div className="space-y-6">

            <Field label="Event Title" name="title" value={eventData.title} onChange={handleInputChange} onBlur={handleFieldBlur} data-field="title" disabled={fieldsDisabled} />
            {fieldErrors.title ? <p className="text-xs text-red-600 -mt-4">{fieldErrors.title}</p> : null}

            <Field label="Subtitle" name="subtitle" value={eventData.subtitle} onChange={handleInputChange} disabled={fieldsDisabled} />

            <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 space-y-3 min-w-0">
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} className="text-brand-maroon mt-0.5 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Venue Policy &amp; Guidelines
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {eventData.venueId
                      ? "Loaded from the selected venue. Edits apply only to this event."
                      : "Select a venue to load its policy, or add rules for this event."}
                  </p>
                </div>
              </div>
              <VenuePolicyEditor
                value={eventPolicyHtml}
                contentKey={policyContentKey}
                disabled={fieldsDisabled}
                onChange={setEventPolicyHtml}
              />
              <p className="text-[11px] text-gray-500">
                This copy is saved with the event. Changing the venue template later will not update it.
              </p>
            </div>

            {/* TICKETS */}
            <div className="space-y-6">

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-field="tickets">
                <h2 className="font-black text-gray-800 uppercase">
                  Ticket and Seats
                </h2>

                <button
                  type="button"
                  onClick={addTicketTier}
                  disabled={fieldsDisabled}
                  className="bg-brand-maroon text-white px-4 py-2 rounded-lg text-xs font-bold uppercase flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={14} /> Add Ticket Tier
                </button>
              </div>

              <div className="bg-white rounded-2xl border overflow-hidden">

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 px-4 py-3 bg-gray-50 text-xs font-bold text-gray-400 uppercase">
                  <span>Ticket Name</span>
                  <span>Price</span>
                  <span>Qty</span>
                  <span>Section</span>
                  <span className="text-center">Actions</span>
                </div>

                <div className="divide-y">
                  {ticketTiers.map((tier, index) => {
                    const seatAreaOptions = getSeatAreasForTier(tier);
                    const usesAutoQuantity = tierHasAutoQuantity(tier);

                    return (
                    <div key={index} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 px-4 py-3 items-start">

                      <div className="min-w-0 space-y-1">
                        <input
                          className="w-full p-2 border rounded text-gray-900 disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                          value={tier.name}
                          disabled={fieldsDisabled}
                          data-field={`ticket-name-${index}`}
                          onChange={(e) => handleTicketChange(index, "name", e.target.value)}
                          onBlur={(e) => handleTicketBlur(index, "name", e.target.value)}
                        />
                        {fieldErrors[`ticket-name-${index}`] ? <p className="text-[10px] text-red-600">{fieldErrors[`ticket-name-${index}`]}</p> : null}
                      </div>

                      <div className="min-w-0 space-y-1">
                      <input
                        type="number"
                        min="0.01"
                        step="any"
                        className="w-full p-2 border rounded text-gray-900 disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                        value={tier.price}
                        disabled={fieldsDisabled}
                        data-field={`ticket-price-${index}`}
                        onChange={(e) => handleTicketChange(index, "price", e.target.value)}
                        onBlur={(e) => handleTicketBlur(index, "price", e.target.value)}
                      />
                      {fieldErrors[`ticket-price-${index}`] ? <p className="text-[10px] text-red-600">{fieldErrors[`ticket-price-${index}`]}</p> : null}
                      </div>

                      <div className="min-w-0 space-y-1">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        readOnly={usesAutoQuantity}
                        disabled={fieldsDisabled}
                        className={`w-full p-2 border rounded text-gray-900 disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed ${usesAutoQuantity ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        value={tier.quantity}
                        data-field={`ticket-qty-${index}`}
                        onChange={(e) => handleTicketChange(index, "quantity", e.target.value)}
                        onBlur={(e) => handleTicketBlur(index, "quantity", e.target.value)}
                      />
                      {fieldErrors[`ticket-qty-${index}`] ? <p className="text-[10px] text-red-600">{fieldErrors[`ticket-qty-${index}`]}</p> : null}
                      </div>

                      <div className="min-w-0 space-y-2">
                        {(() => {
                          const baseOptions = sectionOptions;
                          const hasCurrentSection = baseOptions.some((option) => option.value === tier.section);
                          const options = hasCurrentSection || !tier.section
                            ? baseOptions
                            : [{ value: tier.section, label: tier.section }, ...baseOptions];

                          return (
                        <select
                          value={tier.section}
                          disabled={fieldsDisabled}
                          data-field={`ticket-section-${index}`}
                          onChange={(e) => handleTicketChange(index, "section", e.target.value)}
                          onBlur={(e) => handleTicketBlur(index, "section", e.target.value)}
                          className="w-full p-2 border rounded text-gray-900 cursor-pointer disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                        >
                          <option value="">
                            {eventData.venue
                              ? "Select section"
                              : "Select venue first or choose Music Museum section"}
                          </option>
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                          );
                        })()}

                        {seatAreaOptions.length > 0 && tier.section ? (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Seat Areas</p>
                            {seatAreaOptions.map((option) => (
                              <label key={option.value} className="flex items-center gap-2 text-xs text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={(tier.seatAreas || []).includes(option.value)}
                                  disabled={fieldsDisabled}
                                  onChange={(e) => handleSeatAreaToggle(index, option.value, e.target.checked)}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        ) : null}

                        {!eventData.venue && (
                          <p className="text-[10px] text-slate-500">Select a venue above to filter section options.</p>
                        )}
                        {eventData.venue && !canViewSeatPlan(tier) && tier.section && (
                          <p className="text-[10px] text-slate-500">Seat plan preview is unavailable for this section.</p>
                        )}
                        {eventData.venue && tier.section && seatAreaOptions.length > 0 && !(tier.seatAreas || []).length && (
                          <p className="text-[10px] text-slate-500">Select seat areas to calculate quantity.</p>
                        )}
                        {eventData.venue && !tier.section && (
                          <p className="text-[10px] text-slate-500">Choose a section, then select seat areas and click View Plan.</p>
                        )}

                        {tier.section ? (
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Color</p>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={normalizeHex(tier.color) || DEFAULT_SECTION_COLOR}
                                disabled={fieldsDisabled}
                                onChange={(e) => handleTicketChange(index, "color", e.target.value)}
                                className="h-8 w-10 rounded border border-gray-200 bg-white p-0.5 disabled:opacity-60"
                              />
                              <input
                                type="text"
                                value={tier.color || ""}
                                disabled={fieldsDisabled}
                                onChange={(e) => handleTicketChange(index, "color", e.target.value)}
                                placeholder={DEFAULT_SECTION_COLOR}
                                className="w-24 p-1.5 rounded border text-xs text-gray-900 uppercase disabled:bg-gray-50 disabled:text-gray-600"
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-col items-center gap-2 min-w-0">
                        <button
                          type="button"
                          disabled={!canViewSeatPlan(tier)}
                          onClick={() => {
                            if (!tier.section) return;
                            const previewAreas = tier.seatAreas?.length
                              ? tier.seatAreas
                              : getSeatAreasForTier(tier).map((option) => option.value);
                            setSeatPlanPreviewSection(tier.section);
                            setSeatPlanPreviewAreas(previewAreas);
                            setSeatPlanPageIndex(0);
                            setShowSeatPlanModal(true);
                          }}
                          className="text-xs uppercase px-2 py-1 rounded bg-slate-100 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          View Plan
                        </button>
                        <button
                          type="button"
                          onClick={() => removeTicketTier(index)}
                          disabled={fieldsDisabled}
                          className="disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>

                    </div>
                    );
                  })}
                </div>

              </div>

            </div>

          </div>

          {/* RIGHT */}
          <div className="space-y-6">

            <Field
              label="Event Date"
              type="date"
              name="date"
              value={eventData.date}
              min={getDateInputMinYmd({ originalYmd: isEditMode ? originalEventDate : "" }) || undefined}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              data-field="date"
              disabled={fieldsDisabled}
            />
            {dateError || fieldErrors.date ? <p className="text-xs text-red-600 -mt-4">{dateError || fieldErrors.date}</p> : null}
            <Field label="Event Time" type="time" name="time" value={eventData.time} onChange={handleInputChange} disabled={fieldsDisabled} />
            <SelectField
              label="Venue Name"
              name="venue"
              data-field="venue"
              value={managedVenues.length ? eventData.venueId : eventData.venue}
              onChange={(e) => handleVenueSelect(e.target.value)}
              onBlur={handleVenueBlur}
              options={venueSelectOptions}
              placeholder="Select venue"
              disabled={fieldsDisabled || venueLocked}
            />
            {fieldErrors.venue ? <p className="text-xs text-red-600 -mt-4">{fieldErrors.venue}</p> : null}
            {venueLocked ? (
              <p className="text-xs text-gray-500 -mt-4">Venue is locked because this event already has a saved seat layout.</p>
            ) : null}
            <Field label="Venue Full Address" name="address" value={eventData.address} onChange={handleInputChange} disabled={fieldsDisabled} />
            <SelectField
              label="Status"
              name="status"
              value={eventData.status}
              onChange={handleInputChange}
              disabled={fieldsDisabled}
              options={[
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" },
                { value: "past", label: "Past" },
              ]}
            />

            {/* IMAGE */}
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
                Event Poster Image
              </label>

              <label
                className={`w-full aspect-video border-2 border-dashed rounded-2xl bg-white flex flex-col items-center justify-center overflow-hidden ${
                  fieldsDisabled ? "pointer-events-none opacity-70" : "cursor-pointer"
                }`}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Event Poster" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Upload size={24} className="text-gray-400 mb-2" />
                    <p className="text-xs text-gray-400 uppercase">
                      Upload Image
                    </p>
                  </>
                )}
                <input type="file" hidden disabled={fieldsDisabled} onChange={handleImageSelect} />
              </label>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
                Seat Plan Image (User View)
              </label>

              {!isEditMode &&
              existingSeatPlanImageUrl &&
              !seatPlanImageOverridden &&
              !seatPlanImageFile &&
              !seatPlanImageUrlInput.trim() ? (
                <div className="space-y-3">
                  <div className="w-full aspect-video border-2 border-dashed rounded-2xl bg-white overflow-hidden">
                    <img
                      src={seatPlanImagePreview || existingSeatPlanImageUrl}
                      alt="Seat Plan"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Using seat plan from: {eventData.venue || "selected venue"}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSeatPlanImageOverridden(true)}
                    disabled={fieldsDisabled}
                    className="text-xs font-semibold text-[#8b0000] hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    Replace for this event
                  </button>
                </div>
              ) : (
                <>
                  <label
                    className={`w-full aspect-video border-2 border-dashed rounded-2xl bg-white flex flex-col items-center justify-center overflow-hidden ${
                      fieldsDisabled ? "pointer-events-none opacity-70" : "cursor-pointer"
                    }`}
                  >
                    {seatPlanImagePreview ? (
                      <img src={seatPlanImagePreview} alt="Seat Plan" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <Upload size={24} className="text-gray-400 mb-2" />
                        <p className="text-xs text-gray-400 uppercase">
                          Upload Seat Plan Image
                        </p>
                      </>
                    )}
                    <input type="file" hidden disabled={fieldsDisabled} onChange={handleSeatPlanImageSelect} />
                  </label>

                  <input
                    type="url"
                    disabled={fieldsDisabled}
                    value={seatPlanImageUrlInput}
                    onChange={(e) => {
                      setSeatPlanImageOverridden(true);
                      setSeatPlanImageUrlInput(e.target.value);
                      if (e.target.value.trim()) {
                        setSeatPlanImagePreview(e.target.value.trim());
                      } else {
                        setSeatPlanImagePreview(existingSeatPlanImageUrl || "");
                      }
                    }}
                    placeholder="or paste seat plan image URL"
                    className="w-full p-3 mt-3 rounded-xl border bg-white text-gray-900 text-sm disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                  />
                </>
              )}
            </div>

            {!fieldsDisabled && (
              <button
                disabled={loading}
                className="bg-brand-maroon text-white px-10 py-3 rounded-xl font-black uppercase hover:bg-red-700 disabled:opacity-60 w-full sm:w-auto"
              >
                {loading ? (isEditMode ? "Saving..." : "Publishing...") : (isEditMode ? "Save Changes" : "Publish Event")}
              </button>
            )}

          </div>

        </form>

        {showSaveConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                Save Changes?
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to save these changes to{" "}
                <span className="font-bold text-gray-800">{eventData.title || "this event"}</span>?
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowSaveConfirm(false)}
                  disabled={loading}
                  className="px-4 py-2 min-h-11 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveConfirm(false);
                    handleSubmit();
                  }}
                  disabled={loading}
                  className="px-4 py-2 min-h-11 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDiscardConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                Discard Changes?
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                You have unsaved changes to this event. Leaving now will discard them.
              </p>

              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowDiscardConfirm(false)}
                  className="px-4 py-2 min-h-11 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50"
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    restoreSnapshot();
                    setShowDiscardConfirm(false);
                    setIsLocked(true);
                    navigate(-1);
                  }}
                  className="px-4 py-2 min-h-11 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {showSeatPlanModal && currentSeatPlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="w-full max-w-[92vw] sm:max-w-[50.4rem] rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[90vh]">
              <div className="flex items-start justify-between border-b border-gray-200 p-4 sm:p-5 gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-black break-words">{eventData.venue || "Venue"} — {currentSeatPlan.label}</h2>
                  <p className="text-sm text-gray-500 mt-1 break-words">{currentSeatPlan.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSeatPlanModal(false)}
                  className="text-2xl font-black leading-none text-gray-700 hover:text-black shrink-0"
                >
                  ×
                </button>
              </div>

              <div className="p-4 sm:p-5 overflow-auto max-h-[calc(90vh-5.5rem)]">
                <div className="mb-5 text-xs uppercase tracking-[0.3em] text-gray-500">Seat Plan Preview</div>
                <div className="rounded-3xl border border-gray-200 bg-slate-50 p-5 w-full">
                  <div className="mb-4 flex justify-center">
                    <div className="w-44 rounded-full bg-slate-900 text-white text-center py-2 uppercase text-[10px] tracking-[0.3em]">
                      STAGE
                    </div>
                  </div>

                  {currentSeatPlan.pages ? (
                    <div className="mb-4 flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.3em] text-gray-500">
                        <span>{currentSeatPlan.label} — {currentSeatPage.label}</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSeatPlanPageIndex((prev) => Math.max(prev - 1, 0))}
                            disabled={seatPlanPageIndex === 0}
                            className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase bg-white/90 text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setSeatPlanPageIndex((prev) => Math.min(prev + 1, currentSeatPlan.pages.length - 1))}
                            disabled={seatPlanPageIndex === currentSeatPlan.pages.length - 1}
                            className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase bg-white/90 text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-500">Page {seatPlanPageIndex + 1} of {currentSeatPlan.pages.length}</div>
                    </div>
                  ) : null}

                  <div className="space-y-1.5 flex flex-col items-center">
                    {currentSeatPage.rows.map((row, rowIndex) => {
                      const rowSeats = currentSeatPage.layout?.[rowIndex] || Array.from({ length: currentSeatPage.columns }, () => 1);
                      const leftSeats = rowSeats.slice(0, 12);
                      const rightSeats = rowSeats.slice(12);
                      const seatCellClass = "rounded bg-emerald-300 border border-emerald-500 text-slate-700 flex items-center justify-center";
                      const emptyCellClass = "rounded bg-transparent border border-transparent";
                      const seatCellStyle = {
                        width: `${previewSeatSize}px`,
                        height: `${previewSeatSize}px`,
                        fontSize: `${previewSeatFontSize}px`,
                      };

                      return (
                        <div key={row} className="flex items-center justify-center gap-2">
                          <div style={{ width: `${previewRowLabelWidth}px`, fontSize: `${previewRowLabelFontSize}px` }} className="font-bold text-slate-700">{row}</div>
                          {currentSeatPage.columns === 16 ? (
                            <div className="flex items-center gap-6">
                              <div className="flex items-center" style={{ gap: `${previewSeatGap}px` }}>
                                {leftSeats.map((seat, seatIndex) => (
                                  <div
                                    key={`${row}-${seatIndex + 1}`}
                                    className={seat ? seatCellClass : emptyCellClass}
                                    style={seatCellStyle}
                                  >
                                    {seat
                                      ? currentSeatPlan.canonical
                                        ? previewSeatLabel(row, rowIndex, seatIndex)
                                        : `${seatIndex + 1}${row}`
                                      : ""}
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center" style={{ gap: `${previewSeatGap}px` }}>
                                {rightSeats.map((seat, seatIndex) => (
                                  <div
                                    key={`${row}-${seatIndex + 13}`}
                                    className={seat ? seatCellClass : emptyCellClass}
                                    style={seatCellStyle}
                                  >
                                    {seat
                                      ? currentSeatPlan.canonical
                                        ? previewSeatLabel(row, rowIndex, seatIndex + 12)
                                        : `${seatIndex + 13}${row}`
                                      : ""}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center" style={{ gap: `${previewSeatGap}px` }}>
                              {rowSeats.map((seat, seatIndex) => (
                                <div
                                  key={`${row}-${seatIndex + 1}`}
                                  className={seat ? seatCellClass : emptyCellClass}
                                  style={seatCellStyle}
                                >
                                  {seat
                                    ? currentSeatPlan.canonical
                                      ? previewSeatLabel(row, rowIndex, seatIndex)
                                      : `${seatIndex + 1}${row}`
                                    : ""}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-400" /> Seat
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-900" /> Stage
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
      {label}
    </label>
    <input
      {...props}
      className="w-full p-4 rounded-xl border bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
    />
  </div>
);

const SelectField = ({ label, options, placeholder, ...props }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">
      {label}
    </label>
    <select
      {...props}
      className="w-full p-4 rounded-xl border bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

export default CreateEvent;